import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSync } from "../context/SyncContext";
import { EditableListSelect } from "../components/EditableListSelect";
import { PageHeader, Confirm, EmptyState, FileField, TxFilters, useMeta } from "../components/Shared";
import Money from "../components/Money";
import ReceiptModal from "../components/ReceiptModal";
import { fmtFCFA, fmtDate, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Printer, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import ExportMenu from "../components/ExportMenu";

export default function Income() {
  const { isRole } = useAuth();
  const meta = useMeta();
  const [source, setSource] = useState("");
  const [projectId, setProjectId] = useState("");
  const [fin, setFin] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [refPreview, setRefPreview] = useState("");
  const [externalRef, setExternalRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [fileId, setFileId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", project_id: "all" });
  const canDelete = isRole(ROLES.CEO);

  const qc = useQueryClient();
  const { tick } = useSync();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const incParams = {};
  if (filters.date_from) incParams.date_from = filters.date_from;
  if (filters.date_to) incParams.date_to = filters.date_to;
  if (filters.project_id !== "all") incParams.project_id = filters.project_id;
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["income", filters], queryFn: () => api.get("/income", { params: incParams }).then((r) => r.data) });
  const load = () => qc.invalidateQueries();

  useEffect(() => {
    if (!projectId) { setFin(null); return; }
    api.get(`/projects/${projectId}`).then(({ data }) => setFin({ ...data.financials, name: data.name, total_cost: data.total_cost }));
  }, [projectId]);

  useEffect(() => {
    api.get("/income/next-number", { params: { source } }).then(({ data }) => setRefPreview(data.reference)).catch(() => setRefPreview(""));
  }, [source, rows.length]);

  const submit = async () => {
    if (!projectId || !amount) return;
    setSaving(true);
    try {
      const { data } = await api.post("/income", {
        project_id: projectId, amount: Number(amount), source, payment_method: method,
        external_ref: externalRef, remarks, receipt_file_id: fileId,
      });
      if (data?.pending_approval) {
        toast.info(data.message || "Submitted to the CEO for approval");
        setAmount(""); setRemarks(""); setExternalRef(""); setFileId(null); setSource("");
      } else {
        toast.success("Income recorded");
        setReceipt(data);
        setAmount(""); setRemarks(""); setExternalRef(""); setFileId(null); setSource("");
        load();
        api.get(`/projects/${projectId}`).then(({ data }) => setFin({ ...data.financials, name: data.name, total_cost: data.total_cost }));
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (r) => {
    try { await api.delete(`/income/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Income" subtitle="Record project income and print receipts">
        <ExportMenu dataset="income" filename="stems_income" testId="income-export"
          params={{ date_from: filters.date_from, date_to: filters.date_to, project_id: filters.project_id }} />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add income form */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-heading font-semibold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" /> Add New Income</h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Select Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="income-project"><SelectValue placeholder="Choose a project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {fin && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 text-sm" data-testid="income-project-financials">
                <Row label="Contract Value" value={fmtFCFA(fin.total_cost)} />
                <Row label="Total Income Received" value={fmtFCFA(fin.total_income)} />
                <Row label="Total Expenditure" value={fmtFCFA(fin.total_outflow)} />
                <div className="flex items-center justify-between pt-1 border-t border-slate-200">
                  <span className="text-slate-500">Current Balance</span>
                  <Money value={fin.balance} showIcon={false} />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-sm">Income Amount (FCFA)</Label>
              <Input data-testid="income-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Source</Label>
              <EditableListSelect value={source} onValueChange={setSource} endpoint="/income-sources"
                noun="source" canManage={isRole(ROLES.CEO)} placeholder="Select source" testPrefix="income-source" refreshKey={tick} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="income-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Cash", "Bank Transfer", "Cheque", "Mobile Money"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Reference <span className="text-xs font-normal text-slate-400">· auto by source</span></Label>
                <Input data-testid="income-reference" value={refPreview} readOnly title="Auto-generated from the income source on save"
                  placeholder="Auto-generated" className="bg-slate-50 font-mono-num text-slate-600 cursor-not-allowed" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">External Ref / Transaction No. <span className="text-xs font-normal text-slate-400">· optional</span></Label>
              <Input data-testid="income-external-ref" value={externalRef} onChange={(e) => setExternalRef(e.target.value)}
                placeholder="Bank / MoMo / cheque no." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Remarks</Label>
              <Textarea data-testid="income-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>
            <FileField onUploaded={(id) => setFileId(id)} label="Attach income receipt" testId="income-file" />
            <Button data-testid="add-income-button" onClick={submit} disabled={saving || !projectId || !amount} className="w-full bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" /> Add Income
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-800">Income Records</h3></div>
          <div className="p-4 pb-0"><TxFilters projects={projects} filters={filters} setFilters={setFilters} testPrefix="income-filter" /></div>
          {rowsLoading ? <TableSkeleton cols={7} /> : rows.length === 0 ? <EmptyState text="No income recorded yet." /> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Project</TableHead><TableHead>Source</TableHead><TableHead>Method</TableHead>
                <TableHead>Recorded By</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`income-row-${r.id}`}>
                    <TableCell className="text-slate-600">{fmtDate(r.date)}</TableCell>
                    <TableCell className="font-medium text-slate-800">{r.project_name}</TableCell>
                    <TableCell className="text-slate-600">{r.source || "—"}</TableCell>
                    <TableCell className="text-slate-600">{r.payment_method}</TableCell>
                    <TableCell className="text-slate-600" data-testid={`income-recorded-by-${r.id}`}>
                      <div className="text-slate-800">{r.created_by_name || "—"}</div>
                      {r.created_by_role && <div className="text-xs text-slate-400">{r.created_by_role}</div>}
                    </TableCell>
                    <TableCell className="text-right font-mono-num font-medium text-emerald-700">{fmtFCFA(r.amount)}</TableCell>
                    <TableCell className="text-right">
                      <div className="stems-actions">
                        <Button size="icon" variant="ghost" data-testid={`print-income-${r.id}`} onClick={() => setReceipt(r)}><Printer className="h-4 w-4" /></Button>
                        {canDelete && <Confirm onConfirm={() => remove(r)} title="Delete income record?" description="This removes the income entry and recalculates the project balance."
                          trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-income-${r.id}`}><Trash2 className="h-4 w-4" /></Button>} />}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <ReceiptModal open={!!receipt} onClose={() => setReceipt(null)} type="income" data={receipt} />
    </div>
  );
}
const Row = ({ label, value }) => <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className="font-mono-num font-medium text-slate-800">{value}</span></div>;
